import {
  Component,
  Input,
  OnInit,
  OnChanges,
  OnDestroy,
  AfterViewInit,
  ViewChild,
  ElementRef,
  SimpleChanges,
} from '@angular/core';
import { Subject, forkJoin } from 'rxjs';
import { take, takeUntil } from 'rxjs/operators';
import { ImageLoaderService } from '../../../../shared/services/image-loader.service';
import { D3Chart } from '../../../project/chart-slice/d3.script.heatmap';
import { SliceType } from '../../../../models/sliceType';
import { CachedProjectService } from '../../../../shared/services/cached-project.service';

export type ChartType = 'job1' | 'job2' | 'difference' | 'interleaved';

export interface ComparisonResponse {
  model1?: {
    png: string;
    colorbar?: string;
    slice?: string;
    meta?: any;
    true_min?: number;
    true_max?: number;
  };
  model2?: {
    png: string;
    colorbar?: string;
    slice?: string;
    meta?: any;
    true_min?: number;
    true_max?: number;
  };
  difference?: {
    png: string;
    colorbar?: string;
    slice?: string;
    meta?: any;
  };
  interleave?: {
    png: string;
    slice?: string;
    meta?: any;
  };
  job1?: any;
  job2?: any;
  dimension?: string;
  sliceNumber?: number;
  iteration1?: number;
  iteration2?: number;
  interleavedExtent?: number;
}

@Component({
    selector: 'app-model-visualizer-chart',
    templateUrl: './model-visualizer-chart.component.html',
    styleUrls: ['./model-visualizer-chart.component.less'],
    standalone: false
})
export class ModelVisualizerChartComponent implements OnInit, OnChanges, AfterViewInit, OnDestroy {
  @ViewChild('chart', { static: true }) chart: ElementRef;

  @Input() chartType: ChartType = 'job1';
  @Input() comparisonData: ComparisonResponse | null = null;
  @Input() is2d: boolean = false;
  
  isLoading: boolean = true;
  hasError: boolean = false;
  errorMessage: string = '';

  private destroy$ = new Subject<void>();
  private d3HeatmapChart: D3Chart | null = null;
  private viewInitialized: boolean = false;
  private imageBase64: string | null = null;
  private legendImageBase64: string | null = null;
  private legendImage1Base64: string | null = null;
  private legendImage2Base64: string | null = null;
  private imageRequest: any = null;
  private sliceRequest: any = null;
  private sliceDataCache: Map<string, number[][]> = new Map();
  
  // Static properties to store dimensions across all chart instances
  private static storedWidth: number | null = null;
  private static storedHeight: number | null = null;
  private resizeObserver: ResizeObserver | null = null;
  
  constructor(
    private imageLoaderService: ImageLoaderService,
    private cachedProjectService: CachedProjectService
  ) {}

  ngOnInit(): void {
  }

  ngAfterViewInit(): void {
    
    this.viewInitialized = true;
    
    // Set up ResizeObserver to handle dynamic resizing
    this.setupResizeObserver();
    
    if (this.comparisonData) {
      this.loadChart();
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    
    
    if (!this.viewInitialized || !this.chart) {
      return;
    }
    
    // Only load chart when comparisonData changes(after plot button is clicked)
    if (changes['comparisonData'] && !changes['comparisonData'].firstChange) {
      // Reset stored dimensions when new comparison data arrives
      // in case container size has changed
      ModelVisualizerChartComponent.storedWidth = null;
      ModelVisualizerChartComponent.storedHeight = null;
      this.loadChart();
    } else if (changes['chartType'] && this.comparisonData) {
      // When switching tabs, wait a bit for the tab to become visible
      setTimeout(() => {
        this.loadChart();
      }, 100);
    }
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
    if (this.imageRequest) {
      this.imageRequest.unsubscribe();
    }
    if (this.resizeObserver) {
      this.resizeObserver.disconnect();
    }
  }

  private loadChart(): void {
    if (!this.comparisonData) {
      this.isLoading = false;
      this.hasError = false;
      this.imageBase64 = null;
      this.legendImageBase64 = null;
      this.legendImage1Base64 = null;
      this.legendImage2Base64 = null;
      this.updatePlot();
      return;
    }

    // Cancel any pending requests
    if (this.imageRequest) {
      this.imageRequest.unsubscribe();
      this.imageRequest = null;
    }
    if (this.sliceRequest) {
      this.sliceRequest.unsubscribe();
      this.sliceRequest = null;
    }

    this.isLoading = true;
    this.hasError = false;

    // Get URLs based on chart type
    let imageUrl: string | null = null;
    let colorbarUrl: string | null = null;
    let sliceUrl: string | null = null;

    switch (this.chartType) {
      case 'job1':
        imageUrl = this.comparisonData.model1?.png || null;
        colorbarUrl = this.comparisonData.model1?.colorbar || null;
        sliceUrl = this.comparisonData.model1?.slice || null;
        break;
      case 'job2':
        imageUrl = this.comparisonData.model2?.png || null;
        colorbarUrl = this.comparisonData.model2?.colorbar || null;
        sliceUrl = this.comparisonData.model2?.slice || null;
        break;
      case 'difference':
        imageUrl = this.comparisonData.difference?.png || null;
        colorbarUrl = this.comparisonData.difference?.colorbar || null;
        sliceUrl = this.comparisonData.difference?.slice || null;
        break;
      case 'interleaved':
        imageUrl = this.comparisonData.interleave?.png || null;
        sliceUrl = this.comparisonData.interleave?.slice || null;
        // For interleaved, we don't use a single colorbar but will load both model colorbars separately
        break;
    }

 

    if (!imageUrl) {
      this.isLoading = false;
      this.hasError = true;
      this.errorMessage = "No image available";
      this.imageBase64 = null;
      this.legendImageBase64 = null;
      this.legendImage1Base64 = null;
      this.legendImage2Base64 = null;
      this.updatePlot();
      return;
    }

    // Handle interleaved case with dual colorbars
    if (this.chartType === 'interleaved') {
      const images: any[] = [
        this.imageLoaderService.getImageBase64(imageUrl)
      ];
      
      // Load both model colorbars for interleaved view
      const colorbar1Url = this.comparisonData.model1?.colorbar;
      const colorbar2Url = this.comparisonData.model2?.colorbar;
      
      if (colorbar1Url) {
        images.push(this.imageLoaderService.getImageBase64(colorbar1Url));
      }
      if (colorbar2Url) {
        images.push(this.imageLoaderService.getImageBase64(colorbar2Url));
      }

      this.imageRequest = forkJoin(images)
        .pipe(takeUntil(this.destroy$))
        .subscribe({
          next: (results: string[]) => {
            this.imageBase64 = results[0];
            this.legendImage1Base64 = results[1] || null;
            this.legendImage2Base64 = results[2] || null;
            this.legendImageBase64 = null; // Clear single legend for interleaved
            
            // Load slice data if available
            if (sliceUrl) {
              this.loadSliceData(sliceUrl);
            } else {
              this.isLoading = false;
              this.hasError = false;
              this.updatePlot();
            }
          },
          error: (err) => {
            console.error("Failed to load interleaved images:", err);
            this.errorMessage = "Failed to load image";
            this.hasError = true;
            this.isLoading = false;
            this.imageBase64 = null;
            this.legendImageBase64 = null;
            this.legendImage1Base64 = null;
            this.legendImage2Base64 = null;
            this.updatePlot();
          }
        });
    } else {
      // Handle single colorbar cases (job1, job2, difference)
      const images: any[] = [
        this.imageLoaderService.getImageBase64(imageUrl)  
      ];
      
      if (colorbarUrl) {
        images.push(this.imageLoaderService.getImageBase64(colorbarUrl));
      }

      this.imageRequest = forkJoin(images)
        .pipe(takeUntil(this.destroy$))
        .subscribe({
          next: (results: string[]) => {
            this.imageBase64 = results[0];
            this.legendImageBase64 = results[1] || null;
            this.legendImage1Base64 = null; // Clear dual legends for single cases
            this.legendImage2Base64 = null;
          
            // Load slice data if available
            if (sliceUrl) {
              this.loadSliceData(sliceUrl);
            } else {
              this.isLoading = false;
              this.hasError = false;
              this.updatePlot();
            }
          },
          error: (err) => {
            console.error("Failed to load images:", err);
            this.errorMessage = "Failed to load image";
            this.hasError = true;
            this.isLoading = false;
            this.imageBase64 = null;
            this.legendImageBase64 = null;
            this.legendImage1Base64 = null;
            this.legendImage2Base64 = null;
            this.updatePlot();
          }
        });
    }
  }

  private loadSliceData(sliceUrl: string): void {
     const cacheKey = `${this.chartType}_${sliceUrl}`;

     if (this.sliceDataCache.has(cacheKey)) {
      this.isLoading = false;
      this.hasError = false;
      this.updatePlot();
      return;
    }

    const { xSteps, ySteps } = this.getStepsForSliceType();
    
    const { trueMin, trueMax } = this.getTrueMinMax();

    this.sliceRequest = this.cachedProjectService
      .getSliceArray(sliceUrl, ySteps, xSteps, trueMin, trueMax)
      .pipe(take(1), takeUntil(this.destroy$))
      .subscribe({
        next: (sliceData: number[][]) => {
          
          this.sliceDataCache.set(cacheKey, sliceData);
          this.isLoading = false;
          this.hasError = false;
          this.updatePlot();
        },
        error: (err) => {
          console.warn("Failed to load slice data for hover functionality:", err);
          // Continue without hover data
          this.isLoading = false;
          this.hasError = false;
          this.updatePlot();
        }
      });
  }
  

  private getStepsForSliceType(): { xSteps: number; ySteps: number } {
    const job = this.comparisonData?.job1 || this.comparisonData?.job2;
    if (!job) {
      return { xSteps: 100, ySteps: 100 }; // Default fallback
    }
   
    const x2_is_inline = job.x2_is_inline;
    
    const sliceType = this.getDimensionSliceType();
    
    switch (sliceType) {
      case SliceType.inline:
        return {
          xSteps: job.model_grid__nx3, // getXStepForSliceType logic
          ySteps: x2_is_inline ? job.model_grid__nx2 : job.model_grid__nx1  // getYStepForSliceType logic
        };
      case SliceType.crossLine:
        return {
          xSteps: job.model_grid__nx3,  
          ySteps: x2_is_inline ? job.model_grid__nx1 : job.model_grid__nx2  
        };
      case SliceType.depth:
        return {
          xSteps: job.model_grid__nx1,  
          ySteps: job.model_grid__nx2  
        };
      default:
        return { xSteps: 100, ySteps: 100 };
    }
  }

  private getDimensionSliceType(): SliceType {
    const dimension = this.comparisonData?.dimension || 'Crossline';
    switch (dimension) {
      case 'Inline': 
        return SliceType.inline;
      case 'Crossline': 
        return SliceType.crossLine;
      case 'Depth': 
        return SliceType.depth;
      default: 
        return SliceType.crossLine;
    }
  }

  private getTrueMinMax(): { trueMin: number; trueMax: number } {
    // Get min/max values from the current chart's meta data
    switch (this.chartType) {
      case 'job1':
        return {
          trueMin: this.comparisonData?.model1?.true_min || 0,
          trueMax: this.comparisonData?.model1?.true_max || 1
        };
      case 'job2':
        return {
          trueMin: this.comparisonData?.model2?.true_min || 0,
          trueMax: this.comparisonData?.model2?.true_max || 1
        };
      case 'difference':
        return {
          trueMin: this.comparisonData?.difference?.meta?.true_min || 0,
          trueMax: this.comparisonData?.difference?.meta?.true_max || 1
        };
      case 'interleaved':
        return {
          trueMin: this.comparisonData?.model1?.true_min || 0,
          trueMax: this.comparisonData?.model1?.true_max || 1
        };
      default:
        return { trueMin: 0, trueMax: 1 };
    }
  }

  private getSliceDataForCurrentChart(): number[][] | null {
    if (!this.comparisonData) return null;

    const cacheKey = `${this.chartType}_${this.getSliceUrlForCurrentChart()}`;
    return this.sliceDataCache.get(cacheKey) || null;
  }

  private getSliceUrlForCurrentChart(): string | null {
    switch (this.chartType) {
      case 'job1':
        return this.comparisonData?.model1?.slice || null;
      case 'job2':
        return this.comparisonData?.model2?.slice || null;
      case 'difference':
        return this.comparisonData?.difference?.slice || null;
      case 'interleaved':
        return this.comparisonData?.interleave?.slice || null; 
      default:
        return null;
    }
  }


  private getHoverLabelX(): string {
    const dimension = this.comparisonData?.dimension || 'Crossline';
    switch (dimension) {
      case 'Inline':
        return 'Crossline';
      case 'Crossline':
        return 'Inline';
      case 'Depth':
        return 'Crossline';
      default:
        return 'X';
    }
  }

  private getHoverLabelY(): string {
    const dimension = this.comparisonData?.dimension || 'Crossline';
    switch (dimension) {
      case 'Inline':
      case 'Crossline':
        return 'Depth';
      case 'Depth':
        return 'Inline';
      default:
        return 'Y';
    }
  }

  private getJobNameForInterleavedPosition(xPosition: number, plotWidth: number): string {
  if (this.chartType !== 'interleaved' || !this.comparisonData) {
    return '';
  }

  const extent = this.comparisonData.interleavedExtent || 20;
  const job1Name = this.comparisonData.job1?.job_name || 'Job 1';
  const job2Name = this.comparisonData.job2?.job_name || 'Job 2';

  // Convert mouse position from plot coordinates to image pixel coordinates
  const imagePixelX = Math.floor((xPosition * this.comparisonData.interleave.meta?.shape[1]) / plotWidth);
  
  // Backend logic analysis:
  // - Starts with model1 as base (slice_1)
  // - First model2 section starts at pixel 'extent'
  // - Pattern: model1 [0 to extent-1], model2 [extent to 2*extent-1], 
  //           model1 [2*extent to 3*extent-1], model2 [3*extent to 4*extent-1], etc.
  
  // Calculate which section we're in
  const sectionIndex = Math.floor(imagePixelX / extent);  
  // Even sections (0, 2, 4...) are model1, odd sections (1, 3, 5...) are model2
  return sectionIndex % 2 === 0 ? job1Name : job2Name;
}

  private setupResizeObserver(): void {
    if (!this.chart || !this.chart.nativeElement) {
      return;
    }

    // Set up ResizeObserver to watch for container size changes
    if (typeof ResizeObserver !== 'undefined') {
      this.resizeObserver = new ResizeObserver((entries) => {
        for (const entry of entries) {
          const { width, height } = entry.contentRect;
          if (width > 0 && height > 0) {
            // Store the new dimensions
            ModelVisualizerChartComponent.storedWidth = width;
            ModelVisualizerChartComponent.storedHeight = height;
            
            // Re-render the chart if we have data
            if (this.imageBase64 && this.d3HeatmapChart) {
              this.renderChart();
            }
          }
        }
      });
      
      this.resizeObserver.observe(this.chart.nativeElement);
    }
    
    // Also set up an intersection observer to detect when the tab becomes visible
    if (typeof IntersectionObserver !== 'undefined') {
      const intersectionObserver = new IntersectionObserver((entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting && entry.intersectionRatio > 0) {
            // Re-render when the chart becomes visible (e.g., tab switch)
            if (this.imageBase64 && this.d3HeatmapChart) {
              setTimeout(() => this.renderChart(), 50);
            }
          }
        });
      });
      
      intersectionObserver.observe(this.chart.nativeElement);
      
      // Clean up on destroy
      this.destroy$.subscribe(() => {
        intersectionObserver.disconnect();
      });
    }
  }

  private updatePlot(): void {
    if (!this.chart || !this.chart.nativeElement) {
      return;
    }

    // Wait for next tick to ensure DOM is fully rendered
    setTimeout(() => {
      this.renderChart();
    }, 0);
  }

  private renderChart(): void {
    if (!this.chart || !this.chart.nativeElement) {
      return;
    }

    // Get container dimensions with multiple attempts
    let containerWidth = this.getContainerWidth();
    let containerHeight = this.getContainerHeight();
    

    
    this.createChart(containerWidth, containerHeight);
  }

  private getContainerWidth(): number {
    const element = this.chart.nativeElement;
    
    // Try multiple methods to get width
    let width = element.offsetWidth || 
                element.clientWidth || 
                element.getBoundingClientRect().width;
    
    // If still no width, traverse up the DOM tree
    if (width <= 0) {
      let parent = element.parentElement;
      while (parent && width <= 0) {
        width = parent.offsetWidth || parent.clientWidth || parent.getBoundingClientRect().width;
        parent = parent.parentElement;
      }
    }
    

    if (width <= 0) {
      // Look for tab content container
      const tabContent = element.closest('.ant-tabs-tabpane');
      if (tabContent) {
        const tabContainer = tabContent.closest('.ant-tabs-content-holder');
        if (tabContainer) {
          width = tabContainer.clientWidth || tabContainer.offsetWidth;
          
        }
      }
    }
    
    // Use stored width if available and current is invalid
    if (width <= 0 && ModelVisualizerChartComponent.storedWidth) {
      width = ModelVisualizerChartComponent.storedWidth;
     
    }
    
    // Calculate from viewport if still no width
    if (width <= 0) {
      // Use a percentage of viewport width as fallback
      width = Math.max(window.innerWidth * 0.8, 800);
      
    }
    
    // Store valid width for other charts
    if (width > 0) {
      ModelVisualizerChartComponent.storedWidth = width;
      
    }
    
    return width;
  }

  private getContainerHeight(): number {
    const element = this.chart.nativeElement;
    

    let height = element.offsetHeight || 
                 element.clientHeight || 
                 element.getBoundingClientRect().height;
    

    if (height <= 0) {
      let parent = element.parentElement;
      while (parent && height <= 0) {
        height = parent.offsetHeight || parent.clientHeight || parent.getBoundingClientRect().height;
        parent = parent.parentElement;
      }
    }
    

    if (height <= 0 && ModelVisualizerChartComponent.storedHeight) {
      height = ModelVisualizerChartComponent.storedHeight;
    
    }
    
    // Final fallback
    if (height <= 0) {
      height = 500;
    } else {
      // Store valid height for other charts
      ModelVisualizerChartComponent.storedHeight = height;
   
    }
    
    return height;
  }

  private createChart(containerWidth: number, containerHeight: number): void {
    if (!this.d3HeatmapChart) {
      this.d3HeatmapChart = new D3Chart();
    }

    let reversed = true;

    const attrs = this.d3HeatmapChart.attrs;
    attrs.isModelDiff = true;
    attrs.container = this.chart.nativeElement;
    attrs.svgHeight = containerHeight;
    attrs.svgWidth = containerWidth;
    attrs.data = this.imageBase64;
    attrs.legendImage = this.legendImageBase64;
    attrs.legendVisibility = !!this.legendImageBase64;

    // Add dual colorbar support for interleaved
    if (this.chartType === 'interleaved') {
      const model1Min = this.comparisonData?.model1?.meta?.min_value;
      const model1Max = this.comparisonData?.model1?.meta?.max_value;
      const model2Min = this.comparisonData?.model2?.meta?.min_value;
      const model2Max = this.comparisonData?.model2?.meta?.max_value;
      
      // Check if models have different min/max values (with small tolerance for floating point comparison)
      const tolerance = 1;
      const hasDifferentRanges = Math.abs(model1Min - model2Min) > tolerance || 
                                Math.abs(model1Max - model2Max) > tolerance;
      
      if (hasDifferentRanges && this.legendImage1Base64 && this.legendImage2Base64) {
        // Show dual colorbars when ranges are different
        attrs.dualColorbars = {
          enabled: true,
          colorbar2: this.legendImage2Base64,
          job1Name: this.comparisonData?.job1?.job_name || 'Job 1',
          job2Name: this.comparisonData?.job2?.job_name || 'Job 2',
          model2Min: model2Min,
          model2Max: model2Max
        };
        attrs.legendImage = this.legendImage1Base64;
        attrs.legendVisibility = true;
      } else {
        // Use single colorbar (model 1) when ranges are the same
        attrs.legendImage = this.legendImage1Base64;
        attrs.legendVisibility = !!this.legendImage1Base64;
        attrs.dualColorbars = {
          enabled: false,
        };
      }
    }

    // Add hover functionality attributes
    attrs.hoverLabelX = this.getHoverLabelX();
    attrs.hoverLabelY = this.getHoverLabelY();
    
    // Set the data matrix for hover functionality
    attrs.dataMatrix = this.getSliceDataForCurrentChart();
    
    

    // Add interleaved hover functionality
    if (this.chartType === 'interleaved') {
      attrs.interleavedHover = {
        extent: this.comparisonData?.interleavedExtent || 20,
        job1Name: this.comparisonData?.job1?.job_name || 'Job 1',
        job2Name: this.comparisonData?.job2?.job_name || 'Job 2',
        getJobNameForPosition: this.getJobNameForInterleavedPosition.bind(this)
      };
    }

    // Set grid spacing based on job data
    const job = this.comparisonData?.job1 || this.comparisonData?.job2;
    attrs.gridSpacing = job ? job.model_grid__dx : 1;
    
    attrs.shotsOverlayLegendsVisibility = false;
    attrs.shotOverlayColorRange = ["black", "white"];
    attrs.dotLineX = null;
    attrs.dotLineY = null;
    attrs.dotLineVisibility = false;
    attrs.marginBottom = 112;
    attrs.smoothingEnabled = false;
    attrs.reverseY = true;
    attrs.wellStars = [];
    attrs.shotOverlayData = null;
    attrs.normalLineData = null;
    attrs.solidLineData = [];
    attrs.lightLineData = [];
    attrs.shotLegend = {
      minDomain: 0,
      maxDomain: 2,
      text: ""
    };
    
    
    
    if (job) {
      
      
      // Set dimension-specific grid sizes
      const dimension = this.comparisonData?.dimension || 'Crossline';
      let xSize: number, ySize: number;
      
      switch (dimension) {
        case 'Inline':
          xSize = job.model_grid__nx2 * job.model_grid__dx;
          ySize = job.model_grid__nx3 * job.model_grid__dx;
          break;
        case 'Crossline':
          xSize = job.model_grid__nx1 * job.model_grid__dx;
          ySize = job.model_grid__nx3 * job.model_grid__dx;
          break;
        case 'Depth':
          xSize = job.model_grid__nx2 * job.model_grid__dx;
          ySize = job.model_grid__nx1 * job.model_grid__dx;
          attrs.reverseY = false; // Depth slices don't reverse Y
          break;
        default:
          xSize = job.model_grid__nx2 * job.model_grid__dx;
          ySize = job.model_grid__nx3 * job.model_grid__dx;
      }
      
      attrs.xScale = { maxRange: null, maxDomain: xSize };
      attrs.yScale = { maxRange: null, maxDomain: ySize };
    } else {
      // Default ranges
      attrs.xScale = { maxRange: null, maxDomain: 1000 };
      attrs.yScale = { maxRange: null, maxDomain: 1000 };
    }

    const dimension = this.comparisonData?.dimension || 'Crossline';

    switch (dimension) {
      case 'Inline':
        attrs.xTitle = "Crossline (m)";
        attrs.yTitle = "Depth (m)";
        break;
      case 'Crossline':
        attrs.xTitle = "Inline (m)";
        attrs.yTitle = "Depth (m)";
        break;
      case 'Depth':
        attrs.xTitle = "Crossline (m)";
        attrs.yTitle = "Inline (m)";
        break;
    }


      attrs.legendTitle.text = "velocity";
      attrs.legendTitle.unit = "(m/s)";
    

    switch (this.chartType) {
      case 'job1':
        attrs.yLegend.minDomain = this.comparisonData.model1?.meta?.min_value;
        attrs.yLegend.maxDomain = this.comparisonData.model1?.meta?.max_value;
        break;
      case 'job2':
        attrs.yLegend.minDomain = this.comparisonData.model2?.meta?.min_value;
        attrs.yLegend.maxDomain = this.comparisonData.model2?.meta?.max_value;
        break;
      case 'interleaved':
        // Use model1's min/max for the primary legend
        attrs.yLegend.minDomain = this.comparisonData.model1?.meta?.min_value || 0;
        attrs.yLegend.maxDomain = this.comparisonData.model1?.meta?.max_value || 1;
        break;
      case 'difference':
        attrs.yLegend.minDomain = this.comparisonData.difference?.meta?.min_value;
        attrs.yLegend.maxDomain = this.comparisonData.difference?.meta?.max_value;
        break;
      default:
        attrs.yLegend.minDomain = 0;
        attrs.yLegend.maxDomain = 1;
    }



    setTimeout(() => {
      if (this.d3HeatmapChart) {
        this.d3HeatmapChart.run();
      }
    }, 0);
  }
}